import { useCallback, useMemo, useState, type ReactNode } from 'react';

import { PageAccessCtx, type PageAccess } from '@/hooks/page-access-context';
import { useAuth } from '@/hooks/use-auth';

export function PageAccessProvider({ children }: { readonly children: ReactNode }) {
  const { setDemoMode, user } = useAuth();
  const demoMode = user?.demo_mode === true;
  const [pageDemoMode, setPageDemoMode] = useState(demoMode);
  // While this tab's own change is in flight the session updates before the page follows;
  // that moment is not a change from another tab.
  const [changingHere, setChangingHere] = useState(false);

  const setDemoModeHere = useCallback(async (enabled: boolean): Promise<void> => {
    setChangingHere(true);
    try {
      await setDemoMode(enabled);
    } catch (error) {
      setChangingHere(false);
      throw error;
    }
    setPageDemoMode(enabled);
    setChangingHere(false);
  }, [setDemoMode]);
  const reloadPage = useCallback(() => { setPageDemoMode(demoMode); }, [demoMode]);

  const value = useMemo<PageAccess>(() => ({
    pageDemoMode,
    pageIsStale: !changingHere && pageDemoMode !== demoMode,
    setDemoModeHere,
    reloadPage,
  }), [changingHere, demoMode, pageDemoMode, reloadPage, setDemoModeHere]);

  return <PageAccessCtx value={value}>{children}</PageAccessCtx>;
}
